/**
 * ProfileTabs — 3-icon tab bar with a sliding underline.
 * Web port of client-native/src/features/profile/components/ProfileTabs.tsx.
 *
 * Icons: stats-chart→BarChart3, heart→Heart (filled), time→Clock.
 * Active color primary (#111827), inactive greyLight (#e1e1e1). Underline 2px
 * primary, width = 1/3 of bar, animates 250ms. Bottom border 1px greyLightest.
 */
import { FC } from "react";
import { BarChart3, Heart, Clock } from "lucide-react";
import { colors } from "@/ui";

export type ProfileTabType = "analytics" | "liked" | "history";

interface ProfileTabsProps {
  value: ProfileTabType;
  onChange: (type: ProfileTabType) => void;
}

const TAB_INDEX: Record<ProfileTabType, number> = {
  analytics: 0,
  liked: 1,
  history: 2,
};

const ProfileTabs: FC<ProfileTabsProps> = ({ value, onChange }) => {
  const iconColor = (tab: ProfileTabType) =>
    value === tab ? colors.primary : colors.greyLight;

  const tabs: { type: ProfileTabType; icon: JSX.Element }[] = [
    { type: "analytics", icon: <BarChart3 size={20} color={iconColor("analytics")} /> },
    {
      // iOS uses Ionicons name="heart" (always solid-filled); only the color
      // changes between active (primary) and inactive (greyLight). Fill the
      // heart with the current icon color rather than rendering an outline.
      type: "liked",
      icon: (
        <Heart
          size={20}
          color={iconColor("liked")}
          fill={iconColor("liked")}
        />
      ),
    },
    { type: "history", icon: <Clock size={20} color={iconColor("history")} /> },
  ];

  return (
    <div
      style={{
        position: "relative",
        borderBottom: `1px solid ${colors.greyLightest}`,
      }}
    >
      <div style={{ display: "flex", width: "100%" }}>
        {tabs.map((t) => (
          <button
            key={t.type}
            type="button"
            onClick={() => onChange(t.type)}
            style={{
              flex: 1,
              padding: "10px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "none",
              border: "none",
              cursor: "pointer",
              lineHeight: 0,
            }}
          >
            {t.icon}
          </button>
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 2,
          width: `${100 / 3}%`,
          backgroundColor: colors.primary,
          transform: `translateX(${TAB_INDEX[value] * 100}%)`,
          transition: "transform 250ms ease",
        }}
      />
    </div>
  );
};

export default ProfileTabs;
